import { useEffect, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import {
  can,
  PRICING_BASES,
  PRICING_BASIS_LABEL,
  REMIND_AFTER_MONTHS_MAX,
  REMIND_AFTER_MONTHS_MIN,
  SERVICE_PLAN_KEY,
  SERVICE_TYPE_LABEL_MAX,
  type PricingBasis,
} from '@servwell/shared';
import { reorderKeys } from '@/components/ui/dataTableColumns';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button, iconButton } from '../ui/Button';
import { InlineRename } from '../ui/InlineRename';
import { Toggle } from '../ui/Toggle';
import { PressedGroup } from '../ui/PressedGroup';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { field } from '../create/Field';
import { IconChevronDown, IconChevronUp, IconJobs, IconPlus, IconTrash } from '../icons';

/**
 * The whole list with one row moved a place up (`-1`) or down (`+1`), or null when it has
 * nowhere to go. The server takes the whole order (`serviceType.reorder`), so a move is
 * computed here from the order on screen and sent entire.
 */
export function orderAfterMove(
  ids: readonly string[],
  id: string,
  by: -1 | 1,
): string[] | null {
  const at = ids.indexOf(id);
  const to = ids[at + by];
  if (at === -1 || to === undefined) return null;
  return reorderKeys(ids, id, to);
}

type ServiceTypeList = inferOutput<TRPCOptionsProxy<AppRouter>['serviceType']['list']>;

/**
 * What the card does with the answer to a reorder.
 *
 * Success: the server answers with the list in its new order, and that answer is written
 * straight into the list's cache — synchronously, inside `onSuccess`, which query-core runs
 * BEFORE the mutation stops being pending. So the arrows come back to life over the order the
 * server just stored. Invalidating instead (the first version) marked the list stale and
 * returned; the refetch landed a beat after `busy` had cleared, and a second press in that
 * beat computed its order from the OLD list and undid the first move. Both orders hold the
 * same ids, so the server could not tell. Not a refetch, and never awaited: an `onSuccess`
 * that awaits a read keeps the mutation pending for as long as the read takes, which offline
 * is for ever ("THE FREEZE", settings-states.test.ts).
 *
 * Refusal: say why, and fetch the list again, because the usual reason is that the list
 * changed under this screen and the order on it would only be refused again.
 */
export function reorderHandlers(
  qc: QueryClient,
  trpc: TRPCOptionsProxy<AppRouter>,
  setError: (message: string | null) => void,
) {
  const listKey = trpc.serviceType.list.queryKey();
  return {
    onSuccess: (rows: ServiceTypeList) => {
      setError(null);
      qc.setQueryData(listKey, rows);
    },
    onError: (e: { message: string }) => {
      setError(e.message);
      void qc.invalidateQueries({ queryKey: listKey });
    },
  };
}

/**
 * The shop's service types (ops insights Phase 1, ticket 02) — the settings side, modelled on
 * `CustomFieldsSettingsCard`.
 *
 * Every new job gets one of these, so this list is what revenue is split by. The order here
 * is the order in every picker, which is why a row can be moved.
 *
 * "Service plan" is found by its KEY, never its label (a shop may rename it): jobs booked
 * from service agreements are typed with it, so it has no off switch and no delete. The
 * server refuses both anyway; not drawing them is the difference between a control that
 * works and one that only ever says no.
 */
export function ServiceTypesSettingsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.serviceType.list.queryOptions());
  /**
   * The server's own question (`requirePermission('serviceType', 'manage')`). `/settings` is
   * owner + office today, but opening a page to a role obliges every control on it to ask
   * (LESSONS 2026-08-10) — so a role without the verb gets the list and no controls at all.
   */
  const role = useSession().role;
  const canManage = can(role, 'serviceType', 'manage');
  /**
   * "Remind after N months" emails customers on a schedule (MAP 249 ticket 06): the OWNER's, as
   * every automatic email's switch is (`serviceType.setRemindAfter`, `org:manage`).
   */
  const canRemind = can(role, 'org', 'manage');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  // Fire-and-forget on purpose: awaited, a paused refetch would hold the write pending.
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: trpc.serviceType.list.queryKey() });
  const done = () => {
    setError(null);
    refresh();
  };
  // A refusal refetches too: the usual reason for one is a list that moved under this screen.
  const failed = (e: { message: string }) => {
    setError(e.message);
    refresh();
  };
  const create = useMutation(
    trpc.serviceType.create.mutationOptions({
      onSuccess: () => {
        setDraft('');
        done();
      },
      onError: refresh,
    }),
  );
  /**
   * The last type whose pricing basis this card changed, and the server's count of its open jobs
   * that keep the old basis (booked jobs are never rewritten). Said under that row.
   */
  const [rebased, setRebased] = useState<{
    id: string;
    label: string;
    basis: PricingBasis;
    kept: number;
  } | null>(null);
  const update = useMutation(
    trpc.serviceType.update.mutationOptions({
      onSuccess: (row) => {
        setRebased(
          row.openJobsKeepingBasis == null
            ? null
            : {
                id: row.id,
                label: row.label,
                basis: row.pricingBasis as PricingBasis,
                kept: row.openJobsKeepingBasis,
              },
        );
        done();
      },
      onError: failed,
    }),
  );
  const reorder = useMutation(
    trpc.serviceType.reorder.mutationOptions(reorderHandlers(qc, trpc, setError)),
  );
  const remove = useMutation(
    trpc.serviceType.remove.mutationOptions({ onSuccess: done, onError: failed }),
  );
  const remind = useMutation(
    trpc.serviceType.setRemindAfter.mutationOptions({ onSuccess: done, onError: failed }),
  );
  /**
   * One write at a time. A second press while one saves is ignored by its handler rather
   * than prevented with `disabled`: a control that turns `disabled` while it has focus drops
   * that focus to <body>, and a keyboard user moving a row three places would start again
   * from the top of the page after every press. So each control says `aria-disabled`, and
   * checks `busy` itself. (React keeps focus on a row it MOVES; it is only `disabled` that
   * throws it away.)
   */
  const busy =
    create.isPending ||
    update.isPending ||
    reorder.isPending ||
    remove.isPending ||
    remind.isPending;

  const state = shownState(q, q.data);
  const types = q.data ?? [];
  const ids = types.map((t) => t.id);
  const move = (serviceTypeId: string, by: -1 | 1) => {
    if (busy) return;
    const orderedIds = orderAfterMove(ids, serviceTypeId, by);
    if (orderedIds) reorder.mutate({ orderedIds, ...idem() });
  };

  return (
    <SectionCard id={id} title={counted('Service types', state, types.length)}>
      <p className="mb-3 text-sm text-muted-foreground">
        The kinds of work your shop does. Every new job gets one, so you can see what
        earns your money. Turn one off to stop it being picked for new jobs; the jobs that
        already have it keep it.
      </p>

      {!q.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your service types"
          icon={IconJobs}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : (
        <div className="space-y-3">
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {canManage ? null : (
            <p className="text-xs text-muted-foreground">
              Only an owner or the office can change this list.
            </p>
          )}

          {types.length === 0 ? (
            <p className="text-sm text-muted-foreground">No service types yet.</p>
          ) : (
            <ol className="divide-y divide-border text-sm">
              {types.map((t, i) => {
                const isServicePlan = t.key === SERVICE_PLAN_KEY;
                return (
                  <li
                    key={t.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2"
                  >
                    <div className="min-w-40 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p
                          className={`font-medium ${t.active ? '' : 'text-muted-foreground'}`}
                        >
                          {t.label}
                        </p>
                        {t.active ? null : <StatusBadge tone="neutral">Off</StatusBadge>}
                        {isServicePlan ? (
                          <StatusBadge tone="brand">Always on</StatusBadge>
                        ) : null}
                      </div>
                      {isServicePlan ? (
                        <p className="text-xs text-muted-foreground">
                          Jobs booked from service agreements get this type. You can
                          rename it, but it stays in use and can't be removed.
                        </p>
                      ) : null}
                    </div>

                    {canManage ? (
                      <div className="flex flex-wrap items-center gap-1">
                        <button
                          type="button"
                          aria-label={`Move ${t.label} up`}
                          title="Move up"
                          className={iconButton}
                          aria-disabled={busy || i === 0}
                          onClick={() => move(t.id, -1)}
                        >
                          <IconChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${t.label} down`}
                          title="Move down"
                          className={iconButton}
                          aria-disabled={busy || i === types.length - 1}
                          onClick={() => move(t.id, 1)}
                        >
                          <IconChevronDown size={16} />
                        </button>
                        <InlineRename
                          value={t.label}
                          busy={busy}
                          onSave={(label) =>
                            update.mutate({ serviceTypeId: t.id, label, ...idem() })
                          }
                        />
                        {isServicePlan ? null : (
                          <>
                            <Toggle
                              label="In use"
                              ariaLabel={`${t.label} in use`}
                              on={t.active}
                              disabled={busy}
                              focusableWhenDisabled
                              onChange={(active) =>
                                update.mutate({ serviceTypeId: t.id, active, ...idem() })
                              }
                            />
                            {/* Refused by the server while any job has this type; its
                                answer says to turn it off instead, and shows above. */}
                            <button
                              type="button"
                              aria-label={`Delete ${t.label}`}
                              title="Delete"
                              className={`${iconButton} hover:text-[var(--sw-danger-text)]`}
                              aria-disabled={busy}
                              onClick={() => {
                                if (!busy)
                                  remove.mutate({ serviceTypeId: t.id, ...idem() });
                              }}
                            >
                              <IconTrash size={14} />
                            </button>
                          </>
                        )}
                        {/* What its T&M jobs' hours bill at (ticket 13); blank for the shop's
                            rate. The plan's type is always Fixed, so it bills no hours. */}
                        {isServicePlan ? null : (
                          <HourlyRateField
                            id={`rate-${t.id}`}
                            label={t.label}
                            cents={t.laborRateCents ?? null}
                            busy={busy}
                            onSave={(laborRateCents) =>
                              update.mutate({
                                serviceTypeId: t.id,
                                laborRateCents,
                                ...idem(),
                              })
                            }
                          />
                        )}
                        {/* How its jobs bill parts (Inventory v2 Phase 1). Agreement visits are
                            always Fixed, so the plan's type offers no choice. A press while a
                            write saves is ignored, the card's one-at-a-time rule. */}
                        {isServicePlan ? (
                          <span className="text-xs text-muted-foreground">
                            Fixed · agreement visits are always Fixed
                          </span>
                        ) : (
                          <PressedGroup
                            label={`How ${t.label} jobs price parts`}
                            options={PRICING_BASES.map((value) => ({
                              value,
                              label: PRICING_BASIS_LABEL[value],
                            }))}
                            value={t.pricingBasis as PricingBasis}
                            busy={busy}
                            onChange={(pricingBasis) => {
                              if (busy || pricingBasis === t.pricingBasis) return;
                              update.mutate({
                                serviceTypeId: t.id,
                                pricingBasis,
                                ...idem(),
                              });
                            }}
                          />
                        )}
                      </div>
                    ) : null}
                    {/* The service reminder's months (ticket 06): the owner sets them; anyone
                        else reads them. The plan's type books its own visits: none. */}
                    {isServicePlan ? null : canRemind ? (
                      <RemindAfterField
                        id={`remind-${t.id}`}
                        label={t.label}
                        months={t.remindAfterMonths ?? null}
                        busy={busy}
                        onSave={(remindAfterMonths) =>
                          remind.mutate({
                            serviceTypeId: t.id,
                            remindAfterMonths,
                            ...idem(),
                          })
                        }
                      />
                    ) : t.remindAfterMonths ? (
                      <span className="text-xs text-muted-foreground">
                        Reminder after {t.remindAfterMonths} month
                        {t.remindAfterMonths === 1 ? '' : 's'}
                      </span>
                    ) : null}
                    {rebased?.id === t.id ? (
                      <p
                        role="status"
                        className="basis-full text-xs text-muted-foreground"
                      >
                        {rebasedSentence(rebased)}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}

          {canManage ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const label = draft.trim();
                if (label && !busy) create.mutate({ label, ...idem() });
              }}
            >
              {/* `readOnly` while it saves, not `disabled`: Enter in the box is the usual
                  way to add, and a disabled box would throw away the focus the next name
                  is typed into. */}
              <input
                className={`${field} min-w-0 flex-1 basis-48`}
                value={draft}
                maxLength={SERVICE_TYPE_LABEL_MAX}
                placeholder="Add a type — e.g. Duct cleaning"
                aria-label="New service type"
                readOnly={create.isPending}
                onChange={(e) => {
                  setDraft(e.target.value);
                  // The refusal was about the name that was typed; a new one deserves a
                  // fresh answer, not the old one hanging under it.
                  if (create.error) create.reset();
                }}
              />
              <Button type="submit" aria-disabled={busy || !draft.trim()}>
                <IconPlus size={14} />
                {create.isPending ? 'Adding…' : 'Add'}
              </Button>
              {/* Under the box it answers, not at the top of a list that may have scrolled
                  away on a phone. */}
              {create.error ? (
                <div className="basis-full">
                  <Notice tone="danger">{create.error.message}</Notice>
                </div>
              ) : null}
            </form>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}

/**
 * What Settings says after a type's basis changes: new jobs take it, booked ones keep theirs, and
 * how many of those are still open (the server's count, never a guess).
 */
export function rebasedSentence(r: {
  label: string;
  basis: PricingBasis;
  kept: number;
}): string {
  const now = PRICING_BASIS_LABEL[r.basis];
  const before = PRICING_BASIS_LABEL[r.basis === 'tm' ? 'fixed' : 'tm'];
  const kept =
    r.kept === 0
      ? `No open ${r.label} jobs keep ${before}.`
      : `${r.kept} open ${r.label} ${r.kept === 1 ? 'job keeps' : 'jobs keep'} ${before}; change one on its job page.`;
  return `New ${r.label} jobs are ${now}. ${kept}`;
}

/**
 * A service type's hourly labor rate (Inventory v2 ticket 13): what a T&M job of the type bills
 * its hours at, saved when the box is left or Enter is pressed. Blank is the shop's rate. A save
 * while the card writes something else is ignored, its one-at-a-time rule.
 */
function HourlyRateField({
  id,
  label,
  cents,
  busy,
  onSave,
}: {
  id: string;
  label: string;
  cents: number | null;
  busy: boolean;
  onSave: (cents: number | null) => void;
}) {
  const shown = (c: number | null) => (c === null ? '' : (c / 100).toFixed(2));
  const [text, setText] = useState(shown(cents));
  const input = useRef<HTMLInputElement>(null);
  // The value sent and not yet answered: Enter then leaving the box sends it once, not twice.
  const sent = useRef<number | null | undefined>(undefined);
  // The saved rate changed (this save, or another person's): show it, unless it is being typed.
  useEffect(() => {
    sent.current = undefined;
    if (document.activeElement !== input.current) setText(shown(cents));
  }, [cents]);
  // The card's write settled (saved, or refused): the same value may be sent again.
  useEffect(() => {
    if (!busy) sent.current = undefined;
  }, [busy]);
  const trimmed = text.trim();
  const next = trimmed === '' ? null : Math.round(Number(trimmed) * 100);
  const ok =
    trimmed === '' ||
    (Number.isFinite(Number(trimmed)) &&
      next !== null &&
      next >= 1 &&
      next <= 100_000_000 &&
      Math.abs(Number(trimmed) * 100 - next) < 1e-6);
  const save = () => {
    if (busy || !ok || next === cents || next === sent.current) return;
    sent.current = next;
    onSave(next);
  };
  return (
    // `gap-2`, not `gap-1`: the app's focus ring reaches 5 px past the box (3 px outline + 2 px
    // offset, `theme.css`), so 4 px drew it over "$" and "/h" (MAP 249 QA D9, as Remind after).
    <label className="flex min-h-11 items-center gap-2 text-xs text-muted-foreground">
      <span>Hourly rate</span>
      <span aria-hidden>$</span>
      <input
        ref={input}
        id={id}
        className="h-9 w-20 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-sm tabular-nums text-foreground"
        aria-label={`Hourly labor rate for ${label} jobs, blank for the shop's rate`}
        aria-invalid={ok ? undefined : true}
        aria-describedby={ok ? undefined : `${id}-error`}
        inputMode="decimal"
        placeholder="Shop"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
        }}
      />
      <span aria-hidden>/h</span>
      {ok ? null : (
        <span id={`${id}-error`} className="text-[var(--sw-danger-text)]">
          Type a rate in dollars, like 125
        </span>
      )}
    </label>
  );
}

/**
 * "Remind after N months" (MAP 249 ticket 06): when a customer whose last job of this type was N
 * months ago gets a service reminder, if the shop's switch (Settings → Email) is on. Blank is
 * never. Saved when the box is left or Enter is pressed; a save while the card writes something
 * else is ignored, its one-at-a-time rule.
 */
export function RemindAfterField({
  id,
  label,
  months,
  busy,
  onSave,
}: {
  id: string;
  label: string;
  months: number | null;
  busy: boolean;
  onSave: (months: number | null) => void;
}) {
  const shown = (m: number | null) => (m === null ? '' : String(m));
  const [text, setText] = useState(shown(months));
  const input = useRef<HTMLInputElement>(null);
  const sent = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    sent.current = undefined;
    if (document.activeElement !== input.current) setText(shown(months));
  }, [months]);
  useEffect(() => {
    if (!busy) sent.current = undefined;
  }, [busy]);
  const next = remindAfterOf(text);
  const ok = next !== undefined;
  const save = () => {
    if (busy || next === undefined || next === months || next === sent.current) return;
    sent.current = next;
    onSave(next);
  };
  return (
    // `gap-2`, not `gap-1`: the app's focus ring reaches 5 px past the box (3 px outline + 2 px
    // offset, `theme.css`), so 4 px drew it over "Remind after" and "months" (MAP 249 QA D9).
    <label className="flex min-h-11 items-center gap-2 text-xs text-muted-foreground">
      <span>Remind after</span>
      <input
        ref={input}
        id={id}
        className="h-9 w-14 rounded-[var(--radius-sm)] border border-border bg-surface px-2 text-sm tabular-nums text-foreground"
        aria-label={`Remind ${label} customers after this many months, blank for never`}
        aria-invalid={ok ? undefined : true}
        aria-describedby={ok ? undefined : `${id}-error`}
        inputMode="numeric"
        placeholder="Never"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
        }}
      />
      <span aria-hidden>months</span>
      {ok ? null : (
        <span id={`${id}-error`} className="text-[var(--sw-danger-text)]">
          A whole number from {REMIND_AFTER_MONTHS_MIN} to {REMIND_AFTER_MONTHS_MAX}, or
          blank
        </span>
      )}
    </label>
  );
}

/**
 * What the "Remind after" box says: null for blank (never), the whole number of months from
 * `REMIND_AFTER_MONTHS_MIN` to `REMIND_AFTER_MONTHS_MAX`, or undefined for anything else.
 */
export function remindAfterOf(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return undefined;
  const n = Number(t);
  return n >= REMIND_AFTER_MONTHS_MIN && n <= REMIND_AFTER_MONTHS_MAX ? n : undefined;
}
